/* POS v2 — Salesive-inspired rebuild.
 *
 * Mounted at /pos as of the swap commit. The legacy POS.js still
 * lives in this folder unreferenced — kept as an instant rollback if
 * a regression turns up in prod. Archive once a couple of clean
 * cycles have passed.
 *
 * What's different from the legacy POS:
 *   - Plain top bar (no icon, no subtitle, no stat chips)
 *   - Sale-tab strip (multi-cart). Held orders ARE tabs, not a
 *     separate modal.
 *   - One big search row, horizontal-scroll category strip
 *   - Product cards are image-first with overlay price + Add pills
 *   - Cart panel is a real table (ITEM / QTY / PRICE / TOTAL) with
 *     a sticky footer holding a single Checkout CTA
 *   - Variant / Discount / Payment / Customer are sheets, not modals
 *     (slide-up on mobile, centered card on desktop)
 *
 * Phase 1 — shell only. Loads products, renders the layout. Cart
 * interactions, checkout, customer, variants, payment, multi-cart
 * tabs come in subsequent phases.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateStore } from '../store/storeSlice';
import {
  Search, Package, Plus, X, Trash2, Minus,
  ChevronLeft, ChevronRight, Tag, User, Banknote, List as ListIcon,
  MapPin, ScanLine, LayoutGrid, ShoppingCart,
  Receipt, Wallet, AlertCircle, Lock, TrendingUp,
  Landmark, Copy, Share2, Check,
} from 'lucide-react';
import axios from '../../axiosConfig';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../components/ui/Toast';
import { ROLES } from '../../utils/rolePermissions';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import { calculateMerchantTaxAmount, calculateMerchantTaxAmountToAdd, getQuoteTaxInfo } from '../../utils/merchantTax';
import './POSv2.css';

/* ───────── Module-scope helpers + components ───────── */

const REFUND_REASON_OPTIONS = [
  {
    code: 'CUSTOMER_CANCELLED',
    label: 'Customer request',
    policy: 'Transaction fee stays non-refundable.',
  },
  {
    code: 'CUSTOMER_CHANGED_MIND',
    label: 'Customer changed mind',
    policy: 'Transaction fee stays non-refundable.',
  },
  {
    code: 'MERCHANT_CANNOT_FULFILL',
    label: 'Merchant cannot fulfill',
    policy: 'Customer gets the eligible full refund; merchant absorbs transaction fee.',
  },
  {
    code: 'ITEM_UNAVAILABLE',
    label: 'Item unavailable',
    policy: 'Customer gets the eligible full refund; merchant absorbs transaction fee.',
  },
  {
    code: 'MERCHANT_CANCELLED',
    label: 'Merchant cancelled order',
    policy: 'Customer gets the eligible full refund; merchant absorbs transaction fee.',
  },
];

const newSaleId = () => `sale_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function makeEmptyCart() {
  return {
    id: newSaleId(),
    number: 0, // assigned at creation time by caller (sequential)
    items: [],
    customer: { name: '', phone: '', email: '' },
    createdAt: Date.now(),
  };
}

function cartSubtotal(cart) {
  return (cart?.items || []).reduce((sum, it) => sum + (Number(it.price) || 0) * (it.qty || 0), 0);
}

function cartItemCount(cart) {
  return (cart?.items || []).reduce((sum, it) => sum + (it.qty || 0), 0);
}

/* How many units of (productId, variantId) are already in the active
   cart? Used to gate further adds against available stock. */
function qtyInCart(cart, productId, variantId = null) {
  return (cart?.items || []).reduce((sum, it) => {
    if (it.productId !== productId) return sum;
    if ((it.variantId || null) !== (variantId || null)) return sum;
    return sum + (it.qty || 0);
  }, 0);
}

/* For variant products, available = sum of variant quantities (caller
   can also use a specific variant qty). For non-variant products,
   it's product.quantity. -1 indicates "untracked" — never blocks. */
function availableStock(product, variant = null) {
  if (variant) {
    return Number.isFinite(Number(variant.quantity)) ? Number(variant.quantity) : -1;
  }
  if (product?.hasVariations) {
    // Don't gate at product level — gating happens when the variant
    // is picked. Treat as effectively unlimited at the card click.
    return -1;
  }
  return Number.isFinite(Number(product?.quantity)) ? Number(product.quantity) : -1;
}

/* Sale-tab strip. Multi-cart pattern — every tab is a held sale.
   Right side hosts the today's-stats pills (sales / orders / service
   charge) and the + New button, so the whole top row reads like a
   single navigation bar. */
function SaleTabStrip({
  carts, activeId, onPick, onAdd, onClose, formatMoney,
  todaySummary, feeSummary, canViewFees, onOpenFees,
}) {
  return (
    <div className="posv2-sale-tabs" role="tablist" aria-label="Open sales">
      <div className="posv2-sale-tabs-scroll">
        {carts.map((c) => (
          <div
            key={c.id}
            role="tab"
            aria-selected={c.id === activeId}
            className={`posv2-sale-tab${c.id === activeId ? ' is-active' : ''}`}
          >
            <button
              type="button"
              className="posv2-sale-tab-main"
              onClick={() => onPick(c.id)}
            >
              <span className="posv2-sale-tab-name">Sale #{String(c.number).padStart(4, '0')}</span>
              <span className="posv2-sale-tab-total">{formatMoney(cartSubtotal(c))}</span>
            </button>
            {carts.length > 1 && (
              <button
                type="button"
                className="posv2-sale-tab-close"
                onClick={() => onClose(c.id)}
                aria-label="Close sale"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="posv2-sale-tab-actions">
        <div className="posv2-tab-pill" title="Today's sales">
          <TrendingUp size={12} />
          <span>{formatMoney(todaySummary?.todaySales || 0)}</span>
        </div>
        <div className="posv2-tab-pill" title="Orders today">
          <ShoppingCart size={12} />
          <span>{todaySummary?.todayOrders || 0}</span>
        </div>
        {canViewFees && feeSummary && (
          <button
            type="button"
            className={`posv2-tab-pill posv2-tab-pill--btn${feeSummary.overdueCount > 0 ? ' is-overdue' : ''}`}
            onClick={onOpenFees}
            title="View transaction fees"
          >
            <Wallet size={12} />
            <span>{formatMoney(feeSummary.balance || 0)}</span>
            {feeSummary.overdueCount > 0 && (
              <span className="posv2-tab-pill-badge">{feeSummary.overdueCount}</span>
            )}
          </button>
        )}
        <button type="button" className="posv2-sale-new" onClick={onAdd}>
          <Plus size={14} /> New
        </button>
      </div>
    </div>
  );
}

/* Category strip — horizontal scroll with chevron buttons that nudge
   the strip left/right on click. */
function CategoryStrip({ categories, activeId, onPick }) {
  const scrollerRef = useRef(null);
  const nudge = (dx) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  };

  return (
    <div className="posv2-cat-row">
      <button type="button" className="posv2-cat-nudge" onClick={() => nudge(-200)} aria-label="Scroll left">
        <ChevronLeft size={14} />
      </button>
      <div className="posv2-cat-scroll" ref={scrollerRef}>
        <button
          type="button"
          className={`posv2-cat-chip${activeId === 'all' ? ' is-active' : ''}`}
          onClick={() => onPick('all')}
        >
          All Products
        </button>
        {(categories || []).map((c) => (
          <button
            key={c.id}
            type="button"
            className={`posv2-cat-chip${activeId === c.id ? ' is-active' : ''}`}
            onClick={() => onPick(c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>
      <button type="button" className="posv2-cat-nudge" onClick={() => nudge(200)} aria-label="Scroll right">
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

/* Salesive-style product card.
   - Image fills the top 3/4 of the card
   - Price chip overlays bottom-left of image (white pill)
   - "+ Add" chip overlays bottom-right (dark pill)
   - Below the image: name + mint stock pill on one row */
function ProductCardV2({ product, formatMoney, onAdd, onVariantPick }) {
  const qty = product.quantity ?? 0;
  const isOut = qty <= 0;
  const hasVariants = product.variants?.length > 0;
  const img = product.primaryImage || product.gallery?.[0] || null;

  const handleClick = (e) => {
    e.preventDefault();
    if (hasVariants) {
      onVariantPick(product);
      return;
    }
    if (isOut) return;
    onAdd(product);
  };

  return (
    <article className={`posv2-card${isOut ? ' is-out' : ''}`}>
      <div className="posv2-card-image">
        {img ? <img src={img} alt="" /> : <Package size={28} />}
        <span className="posv2-card-price">{formatMoney(product.price)}</span>
        <button
          type="button"
          className="posv2-card-add"
          onClick={handleClick}
          disabled={isOut && !hasVariants}
          aria-label={hasVariants ? `Pick a variant of ${product.name}` : `Add ${product.name}`}
        >
          <Plus size={12} /> Add
        </button>
      </div>
      <div className="posv2-card-foot">
        <div className="posv2-card-name" title={product.name}>{product.name}</div>
        <div className="posv2-card-meta">
          {hasVariants ? (
            <span className="posv2-card-pill posv2-card-pill--variants">
              {product.variants.length} variants
            </span>
          ) : isOut ? (
            <span className="posv2-card-pill posv2-card-pill--out">Out of stock</span>
          ) : (
            <span className="posv2-card-pill posv2-card-pill--stock">{qty} in stock</span>
          )}
        </div>
      </div>
    </article>
  );
}

/* List-view row — horizontal layout for high-density browsing.
   Same click semantics as the card. */
function ProductListRow({ product, formatMoney, onAdd, onVariantPick }) {
  const qty = product.quantity ?? 0;
  const isOut = qty <= 0;
  const hasVariants = product.variants?.length > 0;
  const img = product.primaryImage || product.gallery?.[0] || null;

  return (
    <button
      type="button"
      className={`posv2-row${isOut ? ' is-out' : ''}`}
      onClick={() => {
        if (hasVariants) { onVariantPick(product); return; }
        if (isOut) return;
        onAdd(product);
      }}
      disabled={isOut && !hasVariants}
    >
      <div className="posv2-row-thumb">
        {img ? <img src={img} alt="" /> : <Package size={18} />}
      </div>
      <div className="posv2-row-info">
        <div className="posv2-row-name">{product.name}</div>
        <div className="posv2-row-sub">
          {hasVariants ? (
            <span className="posv2-card-pill posv2-card-pill--variants">{product.variants.length} variants</span>
          ) : isOut ? (
            <span className="posv2-card-pill posv2-card-pill--out">Out of stock</span>
          ) : (
            <span className="posv2-card-pill posv2-card-pill--stock">{qty} in stock</span>
          )}
        </div>
      </div>
      <div className="posv2-row-price">{formatMoney(product.price)}</div>
      <div className="posv2-row-add" aria-hidden>
        <Plus size={14} />
      </div>
    </button>
  );
}

/* Cart table row */
function CartRow({ item, formatMoney, onQty, onRemove }) {
  return (
    <tr className="posv2-cart-row">
      <td className="posv2-cart-cell posv2-cart-cell--remove">
        <button
          type="button"
          className="posv2-cart-remove"
          onClick={() => onRemove(item.cartItemId)}
          aria-label="Remove"
        >
          <Trash2 size={14} />
        </button>
      </td>
      <td className="posv2-cart-cell posv2-cart-cell--item">
        <div className="posv2-cart-thumb">
          {item.image ? <img src={item.image} alt="" /> : <Package size={14} />}
        </div>
        <div className="posv2-cart-item-text">
          <div className="posv2-cart-item-name">
            {item.name}
            {item.variantName && <span className="posv2-cart-item-variant"> · {item.variantName}</span>}
          </div>
          <div className="posv2-cart-item-sku">SKU: {item.sku || '—'}</div>
        </div>
      </td>
      <td className="posv2-cart-cell posv2-cart-cell--qty">
        <div className="posv2-qty">
          <button type="button" onClick={() => onQty(item.cartItemId, item.qty - 1)} aria-label="Decrease">
            <Minus size={12} />
          </button>
          <span>{item.qty}</span>
          <button type="button" onClick={() => onQty(item.cartItemId, item.qty + 1)} aria-label="Increase">
            <Plus size={12} />
          </button>
        </div>
      </td>
      <td className="posv2-cart-cell posv2-cart-cell--price">{formatMoney(item.price)}</td>
      <td className="posv2-cart-cell posv2-cart-cell--total">{formatMoney(item.price * item.qty)}</td>
    </tr>
  );
}

/* Fee-row status badges. Maps POSFeeTransactionType + POSFeeInvoiceStatus
   to a small pill so an owner can scan the list and see which fees are
   still accruing in their wallet vs settled.

   We deliberately say "Settled" instead of "Wallet-paid" for PAYMENT —
   the API doesn't expose paymentMethod, so we can't distinguish a
   wallet auto-debit from an external Paystack payment without a
   backend change. If that distinction matters later, enrich the
   /pos/fees/summary payload first.

   `kind` keys map 1:1 to CSS modifiers (.posv2-fee-badge--<kind>). */
function getTxBadge(type) {
  switch (String(type || '').toUpperCase()) {
    case 'ACCRUAL':    return { label: 'Accrued in wallet', kind: 'accrued' };
    case 'PAYMENT':    return { label: 'Settled',           kind: 'paid' };
    case 'WAIVER':     return { label: 'Waived',            kind: 'waived' };
    case 'ADJUSTMENT': return { label: 'Adjusted',          kind: 'adjusted' };
    case 'REFUND':     return { label: 'Refunded',          kind: 'refunded' };
    default:           return { label: String(type || '—'), kind: 'neutral' };
  }
}

function getInvoiceBadge(status, dueDate) {
  const s = String(status || '').toUpperCase();
  // PENDING past due date counts as overdue — matches the derivation
  // used by /pos/fees/summary's own overdueCount calculation.
  if (s === 'PENDING' && dueDate && new Date(dueDate) < new Date()) {
    return { label: 'Overdue', kind: 'overdue' };
  }
  switch (s) {
    case 'PENDING':   return { label: 'Pending',   kind: 'accrued' };
    case 'PAID':      return { label: 'Paid',      kind: 'paid' };
    case 'OVERDUE':   return { label: 'Overdue',   kind: 'overdue' };
    case 'WAIVED':    return { label: 'Waived',    kind: 'waived' };
    case 'CANCELLED': return { label: 'Cancelled', kind: 'cancelled' };
    default:          return { label: s || '—',    kind: 'neutral' };
  }
}

/* Blocked banner — shown when /merchant/pos-fees/status returns
   isBlocked. Soft block (UI still loads, lets the merchant scan their
   numbers), the backend rejects POST /pos/order when blocked. */
function BlockedBanner({ reason }) {
  return (
    <div className="posv2-blocked-banner" role="alert">
      <div className="posv2-blocked-icon"><Lock size={18} /></div>
      <div className="posv2-blocked-body">
        <strong>POS access blocked</strong>
        <p>
          Your POS access is paused due to outstanding service fees.
          {reason && <span className="posv2-blocked-reason"> {reason}</span>}
        </p>
      </div>
      <button
        type="button"
        className="posv2-blocked-cta"
        onClick={() => { window.location.href = '/settings?tab=billing'; }}
      >
        Pay now
      </button>
    </div>
  );
}

/* Fee panel — opens from the transaction fee chip. Mirrors v1's
   information: outstanding balance, 4-cell stat grid, recent
   transactions, pending invoices. Wraps in the same posv2-sheet
   pattern so z-index/closing behavior matches the rest of v2. */
function FeePanel({ feeSummary, formatMoney, onClose }) {
  if (!feeSummary) return null;
  const txns = (feeSummary.recentTransactions || []).slice(0, 5);
  const invoices = feeSummary.pendingInvoices || [];

  return (
    <div className="posv2-sheet-wrap" onClick={onClose}>
      <div className="posv2-sheet posv2-sheet--wide posv2-fee-panel" onClick={(e) => e.stopPropagation()}>
        <div className="posv2-sheet-head">
          <h3><Receipt size={16} /> Transaction fees</h3>
          <button type="button" className="posv2-sheet-close" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="posv2-fee-balance">
          <span className="posv2-fee-balance-label">Outstanding Balance</span>
          <span className="posv2-fee-balance-value">{formatMoney(feeSummary.balance || 0)}</span>
          {feeSummary.overdueCount > 0 && (
            <div className="posv2-fee-overdue">
              <AlertCircle size={14} />
              <span>{feeSummary.overdueCount} overdue invoice{feeSummary.overdueCount > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        <div className="posv2-fee-stats">
          <div className="posv2-fee-stat">
            <span className="posv2-fee-stat-label">Total Accrued</span>
            <span className="posv2-fee-stat-value">{formatMoney(feeSummary.totalAccrued || 0)}</span>
          </div>
          <div className="posv2-fee-stat">
            <span className="posv2-fee-stat-label">Total Paid</span>
            <span className="posv2-fee-stat-value is-positive">{formatMoney(feeSummary.totalPaid || 0)}</span>
          </div>
          <div className="posv2-fee-stat">
            <span className="posv2-fee-stat-label">Invoice Cycle</span>
            <span className="posv2-fee-stat-value">{feeSummary.invoiceCycle || 'WEEKLY'}</span>
          </div>
          <div className="posv2-fee-stat">
            <span className="posv2-fee-stat-label">Next Invoice</span>
            <span className="posv2-fee-stat-value">
              {feeSummary.nextInvoiceAt
                ? new Date(feeSummary.nextInvoiceAt).toLocaleDateString()
                : 'N/A'}
            </span>
          </div>
        </div>

        {txns.length > 0 && (
          <div className="posv2-fee-section">
            <h4>Recent Transactions</h4>
            <div className="posv2-fee-list">
              {txns.map((tx) => {
                const badge = getTxBadge(tx.type);
                return (
                  <div key={tx.id} className={`posv2-fee-row posv2-fee-row--${String(tx.type || '').toLowerCase()}`}>
                    <div className="posv2-fee-row-info">
                      <div className="posv2-fee-row-title">
                        <span className="posv2-fee-row-type">{tx.type}</span>
                        <span className={`posv2-fee-badge posv2-fee-badge--${badge.kind}`}>{badge.label}</span>
                      </div>
                      {tx.orderNumber && <span className="posv2-fee-row-meta">#{tx.orderNumber}</span>}
                    </div>
                    <div className="posv2-fee-row-amount">
                      <span className={Number(tx.amount) < 0 ? 'is-refund' : ''}>
                        {Number(tx.amount) < 0 ? '−' : '+'}{formatMoney(Math.abs(Number(tx.amount) || 0))}
                      </span>
                      <span className="posv2-fee-row-date">{new Date(tx.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {invoices.length > 0 && (
          <div className="posv2-fee-section">
            <h4>Pending Invoices</h4>
            <div className="posv2-fee-list">
              {invoices.map((inv) => {
                const badge = getInvoiceBadge(inv.status, inv.dueDate);
                const isOverdue = badge.kind === 'overdue';
                return (
                  <div key={inv.id} className={`posv2-fee-row posv2-fee-row--invoice${isOverdue ? ' is-overdue' : ''}`}>
                    <div className="posv2-fee-row-info">
                      <div className="posv2-fee-row-title">
                        <span className="posv2-fee-row-type">{inv.invoiceNumber}</span>
                        <span className={`posv2-fee-badge posv2-fee-badge--${badge.kind}`}>{badge.label}</span>
                      </div>
                      <span className="posv2-fee-row-meta">
                        {new Date(inv.periodStart).toLocaleDateString()} – {new Date(inv.periodEnd).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="posv2-fee-row-amount">
                      <span>{formatMoney(inv.amount || 0)}</span>
                      <span className="posv2-fee-row-date">Due: {new Date(inv.dueDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────── Main component ───────── */

export default function POSv2() {
  const { isAuthenticated, user } = useSecureAuth();
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatInDisplayCurrency } = useCurrency();
  const formatMoney = useCallback((v) => formatInDisplayCurrency(v ?? 0), [formatInDisplayCurrency]);
  // Read the merchant's POS discovery choice ('active' | 'dismissed' |
  // undefined). Drives the first-visit prompt below.
  const store = useSelector((state) => state.store.current);
  const posOnboardingChoice = store?.settings?.posOnboardingChoice;
  const [showPOSDiscovery, setShowPOSDiscovery] = useState(false);
  const [posDiscoverySaving, setPOSDiscoverySaving] = useState(false);

  // On first visit (no prior choice), show the discovery prompt.
  // If already dismissed, redirect to dashboard — the toggle in Settings
  // > Business preferences is the way back.
  useEffect(() => {
    if (!store) return; // wait for store to load
    if (posOnboardingChoice === 'dismissed') {
      toast.info('POS is hidden. Enable it in Settings > Business preferences.');
      navigate('/dashboard', { replace: true });
    } else if (!posOnboardingChoice) {
      setShowPOSDiscovery(true);
    }
  }, [store, posOnboardingChoice, navigate, toast]);

  const savePOSChoice = async (choice) => {
    setPOSDiscoverySaving(true);
    try {
      const nextSettings = { ...(store?.settings || {}), posOnboardingChoice: choice };
      await dispatch(updateStore({ payload: { settings: nextSettings }, axiosInstance: axios })).unwrap();
      setShowPOSDiscovery(false);
      if (choice === 'dismissed') {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      toast.error('Could not save your choice. Please try again.');
    } finally {
      setPOSDiscoverySaving(false);
    }
  };

  // Role-gated visibility — fee panel is for Owner/Manager only.
  const userRole = user?.merchantRole?.toUpperCase() || ROLES.OWNER;
  const canViewFees = userRole === ROLES.OWNER || userRole === ROLES.MANAGER;

  // Catalog + filter
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  // viewMode: list is mandatory on mobile (grid cards don't pack
   // well at phone widths). User preference persists per session
   // but is always ignored on mobile. Reactive to window resize so
   // rotating a tablet flips the view automatically.
  const detectMobile = () => {
    if (typeof window === 'undefined') return false;
    // Belt + braces — some browsers don't honour matchMedia on
    // hot-reload; innerWidth is the most reliable fallback.
    return window.innerWidth <= 768
        || window.matchMedia('(max-width: 768px)').matches;
  };
  const [isMobile, setIsMobile] = useState(detectMobile);
  useEffect(() => {
    const onResize = () => setIsMobile(detectMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [storedView, setStoredView] = useState(() => {
    const stored = localStorage.getItem('posv2.view');
    return (stored === 'grid' || stored === 'list') ? stored : 'grid';
  });
  const viewMode = isMobile ? 'list' : storedView;
  const toggleView = useCallback(() => {
    setStoredView((m) => {
      const next = m === 'grid' ? 'list' : 'grid';
      localStorage.setItem('posv2.view', next);
      return next;
    });
  }, []);
  // Mobile cart sheet visibility — Phase 1 stub. Click sticky bar
  // to expand.
  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  // Locations
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Merchant settings — loaded with /pos/products. Drives whether
  // merchant tax / transaction fee / tip rows show, and how totals compute.
  const [taxSettings, setTaxSettings] = useState({ enabled: false, rate: 0, name: 'Tax', inclusive: false });
  const [tipSettings, setTipSettings] = useState({ enabled: false });
  const [serviceChargeRate, setServiceChargeRate] = useState(0);
  const [serviceChargeEnabled, setServiceChargeEnabled] = useState(false);
  const [serviceChargeWhoPays, setServiceChargeWhoPays] = useState('customer');
  const [posFeeQuote, setPosFeeQuote] = useState(null);
  const feeQuoteTimerRef = useRef(null);

  // Variant sheet — opens when the merchant taps a variant product.
  const [variantSheetOpen, setVariantSheetOpen] = useState(false);
  const [variantSheetProduct, setVariantSheetProduct] = useState(null);

  // Checkout + sheet state
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [processing, setProcessing] = useState(false);
  const [discountSheetOpen, setDiscountSheetOpen] = useState(false);
  const [customerSheetOpen, setCustomerSheetOpen] = useState(false);
  const [tipSheetOpen, setTipSheetOpen] = useState(false);
  const [refundSheetOpen, setRefundSheetOpen] = useState(false);
  const [refundReasonCode, setRefundReasonCode] = useState('CUSTOMER_CANCELLED');
  const [recentOrders, setRecentOrders] = useState([]);

  // Today's POS performance + service-charge state (Phase 7).
  // todaySummary is read by every user; feeSummary + posAccessStatus
  // matter for Owner/Manager and for backend-enforced fee blocking.
  const [todaySummary, setTodaySummary] = useState({ todaySales: 0, todayOrders: 0 });
  const [feeSummary, setFeeSummary] = useState(null);
  const [feePanelOpen, setFeePanelOpen] = useState(false);
  const [posAccessStatus, setPosAccessStatus] = useState({ isBlocked: false, blockReason: null });

  // Manual Bank Transfer — when the merchant has enabled it AND has a
  // provisioned Paystack Virtual Terminal, tapping Bank Transfer in the
  // payment sheet opens a Transfer Details modal (VT reveal + WhatsApp
  // share) after the order is created. Without config, we fall through
  // to the legacy behavior (order created, confirm-payment later).
  const [manualTransferConfig, setManualTransferConfig] = useState({
    enabled: false,
    virtualTerminal: null,
  });
  const [transferDetailsOrder, setTransferDetailsOrder] = useState(null);
  const [confirmingTransferPayment, setConfirmingTransferPayment] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Barcode scanner — focuses the search input and stamps a visual hint.
  const searchRef = useRef(null);
  const [scanMode, setScanMode] = useState(false);
  const focusSearchForScan = useCallback(() => {
    setScanMode(true);
    searchRef.current?.focus();
    // Drop the visual hint after a moment so it doesn't linger.
    setTimeout(() => setScanMode(false), 1500);
  }, []);

  // Multi-cart tabs
  const [carts, setCarts] = useState(() => {
    const first = makeEmptyCart();
    first.number = 1;
    return [first];
  });
  const [activeCartId, setActiveCartId] = useState(() => null);
  const activeCart = useMemo(() => carts.find((c) => c.id === activeCartId) || carts[0], [carts, activeCartId]);
  useEffect(() => {
    if (!activeCartId && carts.length > 0) setActiveCartId(carts[0].id);
  }, [activeCartId, carts]);

  // Discount + tip + customer live on the active cart (so multi-cart
  // tabs each remember their own). Must be declared AFTER carts/
  // activeCartId — the deps array reads activeCartId on every render.
  const updateActiveDiscount = useCallback((discount) => {
    setCarts((prev) => prev.map((c) => c.id === activeCartId ? { ...c, discount } : c));
  }, [activeCartId]);
  const updateActiveTip = useCallback((tip) => {
    setCarts((prev) => prev.map((c) => c.id === activeCartId ? { ...c, tip } : c));
  }, [activeCartId]);
  const updateActiveCustomer = useCallback((customer) => {
    setCarts((prev) => prev.map((c) => c.id === activeCartId ? { ...c, customer } : c));
  }, [activeCartId]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedLocation?.id) params.append('locationId', selectedLocation.id);
      const response = await axios.get(`/pos/products?${params}`);
      if (response.data.success) {
        setProducts(response.data.products || []);
        setCategories(response.data.categories || []);
        // Pick up the merchant's per-store settings — same shape as
        // legacy POS, drives merchant tax/transaction fee/tip computation.
        if (response.data.taxSettings) setTaxSettings(response.data.taxSettings);
        if (response.data.tipSettings) setTipSettings(response.data.tipSettings);
        if (response.data.serviceChargeRate !== undefined) setServiceChargeRate(Number(response.data.serviceChargeRate));
        if (response.data.serviceChargeEnabled !== undefined) setServiceChargeEnabled(!!response.data.serviceChargeEnabled);
        if (response.data.serviceChargeWhoPays) {
          // Tier defaults are stored UPPERCASE, merchant override is
          // lowercase — see merchantPOSPrisma.getServiceChargeSettings.
          // Backend was patched to normalize, but stay defensive here.
          setServiceChargeWhoPays(String(response.data.serviceChargeWhoPays).toLowerCase());
        }
      }
    } catch (err) {
      console.error('[POSv2] loadProducts failed:', err);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, selectedLocation?.id, toast]);

  // Load locations once. If the merchant has only one location, no
  // chip is rendered — same UX as the legacy POS.
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const { data } = await axios.get('/pos/locations');
        if (data.success) {
          const locs = data.locations || [];
          setLocations(locs);
          // Prefer the one Shell already picked (stored in localStorage),
          // otherwise the primary, otherwise the first.
          const stored = localStorage.getItem('selectedLocationId');
          const pick = locs.find((l) => l.id === stored)
            || locs.find((l) => l.isPrimary)
            || locs[0]
            || null;
          setSelectedLocation(pick);
        }
      } catch (err) {
        console.warn('[POSv2] loadLocations failed:', err?.message);
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) loadProducts();
  }, [isAuthenticated, loadProducts]);

  /* Cart mutations operate on the active cart only */
  const mutateActive = useCallback((mutator) => {
    setCarts((prev) => prev.map((c) => (c.id === activeCart?.id ? mutator(c) : c)));
  }, [activeCart?.id]);

  const addItem = useCallback((product, variant = null) => {
    // Stock validation — block if adding one more would exceed the
    // available stock for this (product, variant) slot. FEFO at the
    // backend will refuse anyway, but blocking at the UI gives the
    // merchant immediate feedback instead of a checkout-time error.
    const available = availableStock(product, variant);
    if (available !== -1) {
      const inCart = qtyInCart(activeCart, product.id, variant?.id || null);
      if (inCart + 1 > available) {
        toast.error(
          `Only ${available} ${variant?.name ? `${variant.name} of ${product.name}` : product.name} in stock`
        );
        return;
      }
    }

    mutateActive((c) => {
      const matchKey = variant ? `${product.id}::${variant.id}` : product.id;
      const existing = c.items.find((it) => it.matchKey === matchKey);
      if (existing) {
        return {
          ...c,
          items: c.items.map((it) => it.matchKey === matchKey ? { ...it, qty: it.qty + 1 } : it),
        };
      }
      const cartItemId = `ci_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      return {
        ...c,
        items: [
          ...c.items,
          {
            cartItemId,
            matchKey,
            productId: product.id,
            variantId: variant?.id || null,
            name: product.name,
            variantName: variant?.name || null,
            sku: variant?.sku || product.sku || null,
            price: Number(variant?.price ?? product.price ?? 0),
            qty: 1,
            // Cache the max so qty stepper inside the cart can gate too,
            // without re-finding the product on every increment.
            maxQty: available === -1 ? null : available,
            image: product.primaryImage || product.gallery?.[0] || null,
          },
        ],
      };
    });
  }, [mutateActive, activeCart, toast]);

  const handleQty = useCallback((cartItemId, qty) => {
    if (qty <= 0) {
      mutateActive((c) => ({ ...c, items: c.items.filter((it) => it.cartItemId !== cartItemId) }));
      return;
    }
    mutateActive((c) => {
      const item = c.items.find((it) => it.cartItemId === cartItemId);
      if (item?.maxQty != null && qty > item.maxQty) {
        toast.error(`Only ${item.maxQty} in stock`);
        return c;
      }
      return {
        ...c,
        items: c.items.map((it) => it.cartItemId === cartItemId ? { ...it, qty } : it),
      };
    });
  }, [mutateActive, toast]);

  const handleRemove = useCallback((cartItemId) => {
    mutateActive((c) => ({ ...c, items: c.items.filter((it) => it.cartItemId !== cartItemId) }));
  }, [mutateActive]);

  const handleClear = useCallback(() => {
    mutateActive((c) => ({ ...c, items: [] }));
  }, [mutateActive]);

  /* Multi-cart tab actions */
  const handleAddSale = useCallback(() => {
    setCarts((prev) => {
      const next = makeEmptyCart();
      next.number = (prev[prev.length - 1]?.number || 0) + 1;
      setActiveCartId(next.id);
      return [...prev, next];
    });
  }, []);

  const handleCloseSale = useCallback((id) => {
    setCarts((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((c) => c.id !== id);
      if (id === activeCartId) {
        setActiveCartId(next[next.length - 1].id);
      }
      return next;
    });
  }, [activeCartId]);

  /* Variant picker — opens a sheet listing every variant of the product
     with name, price, and stock. Click adds that specific variant to
     the active cart. Out-of-stock variants are visually disabled. */
  const handleVariantPick = useCallback((product) => {
    if (!product.variants || product.variants.length === 0) return;
    setVariantSheetProduct(product);
    setVariantSheetOpen(true);
  }, []);

  const pickVariant = useCallback((variant) => {
    if (!variantSheetProduct) return;
    addItem(variantSheetProduct, variant);
    setVariantSheetOpen(false);
    setVariantSheetProduct(null);
  }, [variantSheetProduct, addItem]);

  /* ── Totals — same formula as legacy POS ──────────────────────
     1. discountAmount: percent off subtotal OR absolute amount
     2. subtotalAfterDiscount = subtotal - discountAmount
     3. taxAmount = merchant tax on subtotalAfterDiscount (inclusive-aware)
     4. transaction fee base = subtotalAfterDiscount + exclusive tax
     5. transaction fee added to customer total only when whoPays='customer'
     6. tip added on top
  */
  const subtotal = useMemo(() => cartSubtotal(activeCart), [activeCart]);
  const discount = activeCart?.discount || { type: 'PERCENTAGE', value: 0 };
  const tipAmount = Number(activeCart?.tip || 0);

  const discountAmount = useMemo(() => {
    if (!discount?.value) return 0;
    if (discount.type === 'PERCENTAGE') {
      return Math.min(subtotal, subtotal * (Number(discount.value) / 100));
    }
    return Math.min(subtotal, Number(discount.value));
  }, [discount, subtotal]);

  const subtotalAfterDiscount = subtotal - discountAmount;
  const fallbackTaxAmount = calculateMerchantTaxAmount(subtotalAfterDiscount, taxSettings);
  const fallbackTaxAmountToAdd = calculateMerchantTaxAmountToAdd(subtotalAfterDiscount, taxSettings);
  const fallbackBaseForServiceCharge = subtotalAfterDiscount + fallbackTaxAmountToAdd;
  const fallbackServiceChargeAmount = (serviceChargeEnabled && serviceChargeRate > 0)
    ? fallbackBaseForServiceCharge * Number(serviceChargeRate)
    : 0;
  const effectiveWhoPays = String(posFeeQuote?.serviceChargeWhoPays || serviceChargeWhoPays || 'customer').toLowerCase();
  const quoteTax = getQuoteTaxInfo(posFeeQuote || {});
  const displayTaxSettings = quoteTax.enabled
    ? { name: quoteTax.name, rate: quoteTax.rate, inclusive: quoteTax.inclusive }
    : taxSettings;
  const taxAmount = posFeeQuote ? quoteTax.amount : fallbackTaxAmount;
  const serviceChargeAmount = posFeeQuote ? Number(posFeeQuote.serviceCharge || 0) : fallbackServiceChargeAmount;
  const customerServiceCharge = effectiveWhoPays === 'customer' ? serviceChargeAmount : 0;
  const total = posFeeQuote
    ? Number(posFeeQuote.total || 0) + tipAmount
    : subtotalAfterDiscount + fallbackTaxAmountToAdd + customerServiceCharge + tipAmount;

  useEffect(() => {
    if (feeQuoteTimerRef.current) clearTimeout(feeQuoteTimerRef.current);

    if (subtotalAfterDiscount <= 0) {
      setPosFeeQuote(null);
      return undefined;
    }

    setPosFeeQuote(null);

    feeQuoteTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.post('/pos/fees/quote', {
          subtotal,
          discountAmount
        });
        if (data.success) {
          setPosFeeQuote(data);
        }
      } catch (err) {
        console.warn('[POSv2] fee quote failed:', err?.message);
        setPosFeeQuote(null);
      }
    }, 250);

    return () => {
      if (feeQuoteTimerRef.current) clearTimeout(feeQuoteTimerRef.current);
    };
  }, [subtotal, discountAmount, subtotalAfterDiscount]);

  /* ── Barcode scanner listener ─────────────────────────────────
     Same pattern as legacy POS: rapid keypresses (<100ms apart)
     terminated by Enter are treated as a scan. We resolve via
     GET /pos/scan/:barcode and add the resulting product/variant
     to the active cart. Stays armed whenever the page is focused
     and the merchant isn't typing in a normal input. */
  useEffect(() => {
    let buffer = '';
    let lastKey = Date.now();
    const onKeyPress = async (e) => {
      // Ignore when the user is typing in any input that isn't the
      // search bar (which we want to be barcode-aware). Skip when
      // a sheet is open.
      const tag = e.target?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const isOurSearch = e.target?.getAttribute?.('data-posv2-search') === '1';
      if (isInput && !isOurSearch) return;

      const now = Date.now();
      if (now - lastKey > 100) buffer = '';
      lastKey = now;

      if (e.key === 'Enter' && buffer.length > 3) {
        const code = buffer;
        buffer = '';
        try {
          const { data } = await axios.get(`/pos/scan/${encodeURIComponent(code)}`, {
            params: selectedLocation?.id ? { locationId: selectedLocation.id } : undefined
          });
          if (data.success && data.product) {
            addItem(data.product, data.matchedVariation || null);
            toast.success(`Scanned · ${data.product.name}${data.matchedVariation ? ` · ${data.matchedVariation.name}` : ''}`);
          }
        } catch (err) {
          toast.error(err?.response?.data?.error || 'Product not found for barcode');
        }
      } else if (e.key.length === 1) {
        buffer += e.key;
      }
    };
    window.addEventListener('keypress', onKeyPress);
    return () => window.removeEventListener('keypress', onKeyPress);
  }, [addItem, selectedLocation?.id, toast]);

  /* ── Today's summary + fee/access status ────────────────────
     loadRecentOrders folds in `data.summary` (today's sales + order
     count). loadFeeSummary is Owner/Manager only — the backend gates
     /pos/fees/summary so cashier roles get a 403. checkPosAccessStatus
     surfaces a soft block when /merchant/pos-fees/status returns
     isBlocked; the backend is still the chokepoint, but disabling the
     Checkout button gives instant feedback.

     Declared BEFORE processCheckout because processCheckout's deps
     array references loadRecentOrders + loadFeeSummary — without the
     reorder we'd hit a TDZ on first render. */
  const loadRecentOrders = useCallback(async () => {
    try {
      const { data } = await axios.get('/pos/orders?limit=20');
      if (data.success) {
        setRecentOrders(data.orders || []);
        if (data.summary) setTodaySummary(data.summary);
      }
    } catch (err) {
      console.warn('[POSv2] loadRecentOrders failed:', err?.message);
    }
  }, []);

  const loadFeeSummary = useCallback(async () => {
    if (!canViewFees) return;
    try {
      const { data } = await axios.get('/pos/fees/summary');
      if (data.success) setFeeSummary(data.fees);
    } catch (err) {
      console.warn('[POSv2] loadFeeSummary failed:', err?.message);
    }
  }, [canViewFees]);

  const checkPosAccessStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/merchant/pos-fees/status');
      if (data.success && data.posAccess) {
        setPosAccessStatus({
          isBlocked: !!data.posAccess.isBlocked,
          blockReason: data.posAccess.blockReason || null,
        });
      }
    } catch (err) {
      console.warn('[POSv2] checkPosAccessStatus failed:', err?.message);
    }
  }, []);

  const fetchManualTransferConfig = useCallback(async () => {
    try {
      const { data } = await axios.get('/merchant/payment-gateways/manual-transfer');
      if (data?.status === 'success') {
        const payload = data.data || {};
        const enabled = payload.enabled === true;
        const vt = payload.virtualTerminal || null;
        setManualTransferConfig({
          enabled: enabled && Boolean(vt?.accountNumber),
          virtualTerminal: vt,
        });
      }
    } catch {
      // Silent — merchant just gets the legacy Bank Transfer flow.
      setManualTransferConfig({ enabled: false, virtualTerminal: null });
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadRecentOrders();
    loadFeeSummary();
    checkPosAccessStatus();
    fetchManualTransferConfig();
  }, [isAuthenticated, loadRecentOrders, loadFeeSummary, checkPosAccessStatus, fetchManualTransferConfig]);

  /* ── Checkout ─────────────────────────────────────────────────
     Posts to the same /pos/order endpoint legacy uses. inventoryService
     handles FEFO consumption + per-batch cost stamping server-side. */
  const processCheckout = useCallback(async (method) => {
    if ((activeCart?.items?.length || 0) === 0) {
      toast.error('Cart is empty'); return;
    }
    setProcessing(true);
    try {
      const c = activeCart;
      const orderData = {
        items: c.items.map((it) => ({
          productId: it.productId,
          variantId: it.variantId || null,
          quantity: it.qty,
        })),
        paymentMethod: method,
        customerInfo: (c.customer?.name || c.customer?.phone || c.customer?.email) ? c.customer : null,
        discount: discount.value > 0 ? discount : null,
        tip: tipAmount || 0,
        locationId: selectedLocation?.id || null,
      };
      const { data } = await axios.post('/pos/order', orderData);
      if (data.success) {
        // Bank Transfer + Manual Transfer configured → detour to the
        // Transfer Details modal so the merchant can relay VT bank/account
        // to the customer (in person or via WhatsApp). VT auto-match will
        // still resolve the order without user action if the transfer
        // lands first. Without config, fall through to the legacy toast.
        const isMtFlow = method === 'BANK_TRANSFER'
          && manualTransferConfig.enabled
          && manualTransferConfig.virtualTerminal?.accountNumber;

        if (isMtFlow) {
          setTransferDetailsOrder({
            id: data.order?.id,
            orderNumber: data.order?.orderNumber,
            total: data.order?.total ?? total,
          });
          mutateActive((cart) => ({ ...cart, items: [], discount: undefined, tip: 0, customer: { name: '', phone: '', email: '' } }));
          setPaymentSheetOpen(false);
          loadProducts();
          loadRecentOrders();
          loadFeeSummary();
        } else {
          toast.success(`Sale #${data.order?.orderNumber || ''} ${method === 'CASH' ? 'completed' : 'created'}`);
          mutateActive((cart) => ({ ...cart, items: [], discount: undefined, tip: 0, customer: { name: '', phone: '', email: '' } }));
          setPaymentSheetOpen(false);
          loadProducts();
          loadRecentOrders();
          loadFeeSummary();
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  }, [activeCart, discount, tipAmount, selectedLocation?.id, mutateActive, loadProducts, loadRecentOrders, loadFeeSummary, toast, manualTransferConfig, total]);

  /* ── Refund ────────────────────────────────────────────────── */

  const openRefund = useCallback(() => {
    loadRecentOrders();
    setRefundSheetOpen(true);
  }, [loadRecentOrders]);

  const processRefund = useCallback(async (orderId) => {
    const reason = REFUND_REASON_OPTIONS.find((option) => option.code === refundReasonCode) || REFUND_REASON_OPTIONS[0];
    try {
      const { data } = await axios.post('/pos/refund', {
        orderId,
        items: null, // full-order refund for v2 quick path
        reason: reason.label,
        refundReasonCode: reason.code,
        refundMethod: 'CASH',
        restockItems: true,
      });
      if (data.success) {
        toast.success(`Refunded ${formatMoney(data.refund?.amount || 0)}`);
        setRefundSheetOpen(false);
        loadProducts();
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Refund failed');
    }
  }, [formatMoney, loadProducts, refundReasonCode, toast]);

  return (
    <Shell>
      <FeatureGate
        featureId="sales.pos"
        upgradeMessage="Upgrade to access Point of Sale for in-store sales management"
      >
        <div className="posv2-root">
          {/* Sale-tab strip — multi-cart + today's stats + fee chip.
              Owner/Manager get the clickable service-charge pill;
              Cashiers see only sales/orders. */}
          <SaleTabStrip
            carts={carts}
            activeId={activeCart?.id}
            onPick={setActiveCartId}
            onAdd={handleAddSale}
            onClose={handleCloseSale}
            formatMoney={formatMoney}
            todaySummary={todaySummary}
            feeSummary={feeSummary}
            canViewFees={canViewFees}
            onOpenFees={() => setFeePanelOpen(true)}
          />

          {posAccessStatus.isBlocked && (
            <BlockedBanner reason={posAccessStatus.blockReason} />
          )}

          <div className="posv2-body">
            {/* LEFT — products */}
            <div className="posv2-products">
              <div className="posv2-search-row">
                {locations.length > 1 && (
                  <label className="posv2-loc">
                    <MapPin size={14} />
                    <select
                      value={selectedLocation?.id || ''}
                      onChange={(e) => {
                        const loc = locations.find((l) => l.id === e.target.value);
                        setSelectedLocation(loc);
                        if (loc) {
                          localStorage.setItem('selectedLocationId', loc.id);
                          window.dispatchEvent(new CustomEvent('locationChanged', { detail: loc }));
                        }
                      }}
                    >
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}{loc.code ? ` (${loc.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className={`posv2-search${scanMode ? ' is-scanning' : ''}`}>
                  <Search size={16} />
                  <input
                    ref={searchRef}
                    type="text"
                    data-posv2-search="1"
                    placeholder="Scan barcode or search product"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="posv2-search-hint">F2</span>
                </div>
                <button
                  type="button"
                  className="posv2-search-scan"
                  onClick={focusSearchForScan}
                  title="Scan barcode"
                  aria-label="Scan barcode"
                >
                  <ScanLine size={16} />
                </button>
                <button
                  type="button"
                  className={`posv2-search-list${viewMode === 'list' ? ' is-active' : ''}`}
                  onClick={toggleView}
                  title={viewMode === 'grid' ? 'List view' : 'Grid view'}
                  aria-label="Toggle view"
                >
                  {viewMode === 'grid' ? <ListIcon size={16} /> : <LayoutGrid size={16} />}
                </button>
              </div>

              <CategoryStrip
                categories={categories}
                activeId={selectedCategory}
                onPick={setSelectedCategory}
              />

              <div className={viewMode === 'list' ? 'posv2-list' : 'posv2-grid'}>
                {loading ? (
                  <div className="posv2-empty">Loading products…</div>
                ) : products.length === 0 ? (
                  <div className="posv2-empty">No products found</div>
                ) : viewMode === 'list' ? (
                  products.map((p) => (
                    <ProductListRow
                      key={p.id}
                      product={p}
                      formatMoney={formatMoney}
                      onAdd={addItem}
                      onVariantPick={handleVariantPick}
                    />
                  ))
                ) : (
                  products.map((p) => (
                    <ProductCardV2
                      key={p.id}
                      product={p}
                      formatMoney={formatMoney}
                      onAdd={addItem}
                      onVariantPick={handleVariantPick}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Mobile sticky cart bar — taps open the cart sheet.
                Hidden on desktop via CSS. */}
            <button
              type="button"
              className="posv2-mobile-cart-bar"
              onClick={() => setMobileCartOpen(true)}
              aria-label="Open cart"
            >
              <ShoppingCart size={16} />
              <span className="posv2-mobile-cart-count">{cartItemCount(activeCart)} item{cartItemCount(activeCart) === 1 ? '' : 's'}</span>
              <span className="posv2-mobile-cart-total">{formatMoney(subtotal)}</span>
            </button>

            {/* Mobile backdrop */}
            {mobileCartOpen && (
              <div className="posv2-mobile-backdrop" onClick={() => setMobileCartOpen(false)} />
            )}

            {/* RIGHT — cart panel (also serves as mobile bottom sheet) */}
            <aside className={`posv2-cart${mobileCartOpen ? ' is-open' : ''}`}>
              <div className="posv2-cart-head">
                <div className="posv2-cart-title">
                  Current Sale <span className="posv2-cart-id">#{String(activeCart?.number ?? 1).padStart(4, '0')}</span>
                  <span className="posv2-cart-pill">{cartItemCount(activeCart)} {cartItemCount(activeCart) === 1 ? 'Item' : 'Items'}</span>
                </div>
                <div className="posv2-cart-head-actions">
                  {(activeCart?.items?.length || 0) > 0 && (
                    <button type="button" className="posv2-cart-clear" onClick={handleClear}>
                      <Trash2 size={12} /> Clear Cart
                    </button>
                  )}
                  <button
                    type="button"
                    className="posv2-cart-close"
                    onClick={() => setMobileCartOpen(false)}
                    aria-label="Close cart"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              <div className="posv2-cart-table-wrap">
                {(activeCart?.items?.length || 0) === 0 ? (
                  <div className="posv2-cart-empty">
                    <Package size={28} />
                    <span>Cart is empty</span>
                    <span className="posv2-cart-empty-hint">Add products to start a sale</span>
                  </div>
                ) : (
                  <table className="posv2-cart-table">
                    <colgroup>
                      <col style={{ width: 28 }} />
                      <col />
                      <col style={{ width: 78 }} />
                      <col style={{ width: 72 }} />
                      <col style={{ width: 82 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th />
                        <th className="posv2-cart-th-item">Item</th>
                        <th className="posv2-cart-th-num">Qty</th>
                        <th className="posv2-cart-th-num">Price</th>
                        <th className="posv2-cart-th-num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeCart.items.map((it) => (
                        <CartRow
                          key={it.cartItemId}
                          item={it}
                          formatMoney={formatMoney}
                          onQty={handleQty}
                          onRemove={handleRemove}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Sticky footer — real totals breakdown */}
              <div className="posv2-cart-foot">
                <div className="posv2-cart-breakdown">
                  <div className="posv2-cart-line">
                    <span>Subtotal</span>
                    <span>{formatMoney(subtotal)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="posv2-cart-line posv2-cart-line--neg">
                      <span>Discount</span>
                      <span>-{formatMoney(discountAmount)}</span>
                    </div>
                  )}
                  {taxAmount > 0 && (
                    <div className="posv2-cart-line">
                      <span>{displayTaxSettings.name || 'Tax'} ({Number(displayTaxSettings.rate)}%{displayTaxSettings.inclusive ? ' incl.' : ''})</span>
                      <span>{displayTaxSettings.inclusive ? formatMoney(taxAmount) : `+${formatMoney(taxAmount)}`}</span>
                    </div>
                  )}
                  {/* POS shows the merchant the transaction fee even
                      when it is not added to the customer total. */}
                  {serviceChargeAmount > 0 && (
                    <div className="posv2-cart-line">
                      <span>
                        Transaction fee
                        {effectiveWhoPays !== 'customer' && (
                          <span className="posv2-cart-line-note"> · merchant pays</span>
                        )}
                      </span>
                      <span>{effectiveWhoPays === 'customer' ? `+${formatMoney(serviceChargeAmount)}` : `(${formatMoney(serviceChargeAmount)})`}</span>
                    </div>
                  )}
                  {tipAmount > 0 && (
                    <div className="posv2-cart-line">
                      <span>Tip</span>
                      <span>+{formatMoney(tipAmount)}</span>
                    </div>
                  )}
                </div>
                <div className="posv2-cart-total-row">
                  <span className="posv2-cart-total-label">Total</span>
                  <span className="posv2-cart-total-value">{formatMoney(total)}</span>
                </div>
                <div className="posv2-cart-foot-actions">
                  <button type="button" className="posv2-foot-pill" onClick={() => setDiscountSheetOpen(true)}>
                    <Tag size={14} /> {discount.value > 0 ? `Discount: ${discount.type === 'PERCENTAGE' ? `${discount.value}%` : formatMoney(discount.value)}` : 'Add Discount'}
                  </button>
                  <button type="button" className="posv2-foot-pill" onClick={() => setCustomerSheetOpen(true)}>
                    <User size={14} /> {activeCart?.customer?.name || activeCart?.customer?.phone ? 'Customer ✓' : 'Add Customer'}
                  </button>
                </div>
                {tipSettings?.enabled && (
                  <button type="button" className="posv2-foot-pill" onClick={() => setTipSheetOpen(true)}>
                    + Add Tip {tipAmount > 0 && `(${formatMoney(tipAmount)})`}
                  </button>
                )}
                <div className="posv2-cart-foot-actions">
                  <button type="button" className="posv2-foot-pill posv2-foot-pill--danger" onClick={openRefund}>
                    Refund
                  </button>
                  <button
                    type="button"
                    className="posv2-checkout"
                    onClick={() => setPaymentSheetOpen(true)}
                    disabled={(activeCart?.items?.length || 0) === 0 || posAccessStatus.isBlocked}
                    title={posAccessStatus.isBlocked ? 'POS blocked — settle outstanding fees first' : undefined}
                  >
                    <Banknote size={13} />
                    <span>{posAccessStatus.isBlocked ? 'Blocked' : 'Checkout'}</span>
                    <span className="posv2-checkout-key">F5</span>
                  </button>
                </div>
              </div>
            </aside>
          </div>

          {/* ── Sheets ──────────────────────────────────────── */}

          {/* Variant picker sheet */}
          {variantSheetOpen && variantSheetProduct && (
            <div className="posv2-sheet-wrap" onClick={() => { setVariantSheetOpen(false); setVariantSheetProduct(null); }}>
              <div className="posv2-sheet posv2-sheet--wide" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Choose a variant</h3>
                  <button type="button" className="posv2-sheet-close" onClick={() => { setVariantSheetOpen(false); setVariantSheetProduct(null); }}>
                    <X size={14} />
                  </button>
                </div>
                <div className="posv2-variant-header">
                  <div className="posv2-variant-thumb">
                    {(variantSheetProduct.primaryImage || variantSheetProduct.gallery?.[0])
                      ? <img src={variantSheetProduct.primaryImage || variantSheetProduct.gallery?.[0]} alt="" />
                      : <Package size={22} />}
                  </div>
                  <div className="posv2-variant-header-text">
                    <div className="posv2-variant-product-name">{variantSheetProduct.name}</div>
                    <div className="posv2-variant-product-base">
                      Base price · {formatMoney(variantSheetProduct.price)}
                    </div>
                  </div>
                </div>
                <div className="posv2-variant-grid">
                  {variantSheetProduct.variants.map((v) => {
                    const stock = Number.isFinite(Number(v.quantity)) ? Number(v.quantity) : null;
                    const isOut = stock !== null && stock <= 0;
                    const inCartAlready = qtyInCart(activeCart, variantSheetProduct.id, v.id);
                    const atLimit = stock !== null && inCartAlready >= stock;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        className={`posv2-variant-card${(isOut || atLimit) ? ' is-out' : ''}`}
                        onClick={() => pickVariant(v)}
                        disabled={isOut || atLimit}
                      >
                        <div className="posv2-variant-card-name">{v.name}</div>
                        <div className="posv2-variant-card-foot">
                          <span className="posv2-variant-card-price">{formatMoney(v.price)}</span>
                          {stock === null ? (
                            <span className="posv2-card-pill posv2-card-pill--stock">Available</span>
                          ) : isOut ? (
                            <span className="posv2-card-pill posv2-card-pill--out">Out</span>
                          ) : atLimit ? (
                            <span className="posv2-card-pill posv2-card-pill--out">Max in cart</span>
                          ) : (
                            <span className="posv2-card-pill posv2-card-pill--stock">{stock} in stock</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Payment sheet — Cash / Bank transfer */}
          {paymentSheetOpen && (
            <div className="posv2-sheet-wrap" onClick={() => !processing && setPaymentSheetOpen(false)}>
              <div className="posv2-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Select payment</h3>
                  <button type="button" className="posv2-sheet-close" onClick={() => setPaymentSheetOpen(false)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="posv2-sheet-amount">
                  <span>Amount due</span>
                  <strong>{formatMoney(total)}</strong>
                </div>
                <div className="posv2-pay-methods">
                  <button
                    type="button"
                    className="posv2-pay-method"
                    onClick={() => processCheckout('CASH')}
                    disabled={processing}
                  >
                    <Banknote size={22} />
                    <span>Cash</span>
                    <span className="posv2-pay-method-sub">Paid in hand</span>
                  </button>
                  <button
                    type="button"
                    className="posv2-pay-method"
                    onClick={() => processCheckout('BANK_TRANSFER')}
                    disabled={processing}
                  >
                    <Landmark size={22} />
                    <span>Bank Transfer</span>
                    <span className="posv2-pay-method-sub">
                      {manualTransferConfig.enabled
                        ? 'Auto-confirm on receipt'
                        : 'Confirm when received'}
                    </span>
                  </button>
                </div>
                {processing && <div className="posv2-sheet-processing">Processing…</div>}
              </div>
            </div>
          )}

          {/* Transfer Details modal — shown after a BANK_TRANSFER order is
              created for merchants with Manual Transfer configured. Merchant
              relays VT details to customer (in person or via WhatsApp);
              webhook auto-matcher resolves the order when the transfer
              lands, or merchant can confirm manually. */}
          {transferDetailsOrder && (
            <div className="posv2-sheet-wrap" onClick={() => setTransferDetailsOrder(null)}>
              <div className="posv2-sheet posv2-mt-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Awaiting transfer</h3>
                  <button type="button" className="posv2-sheet-close" onClick={() => setTransferDetailsOrder(null)}>
                    <X size={16} />
                  </button>
                </div>

                <div className="posv2-mt-order">
                  <span>Order</span>
                  <strong>#{transferDetailsOrder.orderNumber}</strong>
                </div>

                <div className="posv2-mt-amount">
                  <div className="posv2-mt-amount-label">Amount</div>
                  <div className="posv2-mt-amount-row">
                    <span className="posv2-mt-amount-value">{formatMoney(transferDetailsOrder.total)}</span>
                    <button
                      type="button"
                      className="posv2-mt-copy"
                      onClick={async () => {
                        await navigator.clipboard.writeText(String(transferDetailsOrder.total));
                        setCopiedField('amount');
                        toast.success('Amount copied');
                        setTimeout(() => setCopiedField(null), 1500);
                      }}
                    >
                      {copiedField === 'amount' ? <Check size={14} /> : <Copy size={14} />}
                      {copiedField === 'amount' ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="posv2-mt-details">
                  <div className="posv2-mt-row">
                    <span className="posv2-mt-row-label">Bank</span>
                    <span className="posv2-mt-row-value">{manualTransferConfig.virtualTerminal?.bank || '—'}</span>
                  </div>
                  <div className="posv2-mt-row">
                    <span className="posv2-mt-row-label">Account number</span>
                    <span className="posv2-mt-row-value posv2-mt-row-mono">
                      {manualTransferConfig.virtualTerminal?.accountNumber || '—'}
                      <button
                        type="button"
                        className="posv2-mt-copy posv2-mt-copy-inline"
                        onClick={async () => {
                          const acc = manualTransferConfig.virtualTerminal?.accountNumber || '';
                          if (!acc) return;
                          await navigator.clipboard.writeText(acc);
                          setCopiedField('acc');
                          toast.success('Account number copied');
                          setTimeout(() => setCopiedField(null), 1500);
                        }}
                      >
                        {copiedField === 'acc' ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </span>
                  </div>
                  <div className="posv2-mt-row">
                    <span className="posv2-mt-row-label">Account name</span>
                    <span className="posv2-mt-row-value">{manualTransferConfig.virtualTerminal?.accountName || '—'}</span>
                  </div>
                </div>

                <div className="posv2-mt-actions">
                  <button
                    type="button"
                    className="posv2-mt-btn posv2-mt-btn-secondary"
                    onClick={() => {
                      const vt = manualTransferConfig.virtualTerminal;
                      if (!vt) return;
                      const msg =
                        `Please transfer ${formatMoney(transferDetailsOrder.total)} to:\n\n` +
                        `Bank: ${vt.bank}\n` +
                        `Account Number: ${vt.accountNumber}\n` +
                        `Account Name: ${vt.accountName}\n\n` +
                        `Order: #${transferDetailsOrder.orderNumber}`;
                      // Prefer web-share on mobile browsers, fall back to
                      // wa.me deep link on desktop.
                      const waUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
                      window.open(waUrl, '_blank', 'noopener');
                    }}
                  >
                    <Share2 size={16} />
                    Share via WhatsApp
                  </button>
                  <button
                    type="button"
                    className="posv2-mt-btn posv2-mt-btn-primary"
                    disabled={confirmingTransferPayment || !transferDetailsOrder.id}
                    onClick={async () => {
                      if (!transferDetailsOrder.id) return;
                      try {
                        setConfirmingTransferPayment(true);
                        const { data } = await axios.post(
                          `/pos/order/${transferDetailsOrder.id}/confirm-payment`,
                          { reference: `MANUAL-${Date.now()}` }
                        );
                        if (data?.success) {
                          toast.success('Payment confirmed');
                          setTransferDetailsOrder(null);
                          loadRecentOrders();
                          loadFeeSummary();
                        } else {
                          toast.error(data?.error || 'Failed to confirm payment');
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.error || 'Failed to confirm payment');
                      } finally {
                        setConfirmingTransferPayment(false);
                      }
                    }}
                  >
                    {confirmingTransferPayment ? 'Confirming…' : "I've received payment"}
                  </button>
                </div>

                <button
                  type="button"
                  className="posv2-mt-close-link"
                  onClick={() => setTransferDetailsOrder(null)}
                >
                  Close (auto-confirms when payment lands)
                </button>
              </div>
            </div>
          )}

          {/* Discount sheet */}
          {discountSheetOpen && (
            <div className="posv2-sheet-wrap" onClick={() => setDiscountSheetOpen(false)}>
              <div className="posv2-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Apply discount</h3>
                  <button type="button" className="posv2-sheet-close" onClick={() => setDiscountSheetOpen(false)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="posv2-discount-types">
                  <button
                    type="button"
                    className={`posv2-discount-type${discount.type === 'PERCENTAGE' ? ' is-active' : ''}`}
                    onClick={() => updateActiveDiscount({ ...discount, type: 'PERCENTAGE' })}
                  >Percentage (%)</button>
                  <button
                    type="button"
                    className={`posv2-discount-type${discount.type === 'AMOUNT' ? ' is-active' : ''}`}
                    onClick={() => updateActiveDiscount({ ...discount, type: 'AMOUNT' })}
                  >Amount (₦)</button>
                </div>
                <input
                  type="number"
                  min="0"
                  className="posv2-sheet-input"
                  placeholder={discount.type === 'PERCENTAGE' ? 'e.g. 10' : 'e.g. 5000'}
                  value={discount.value || ''}
                  onChange={(e) => updateActiveDiscount({ ...discount, value: Number(e.target.value) })}
                />
                <div className="posv2-sheet-actions">
                  <button
                    type="button"
                    className="posv2-foot-pill"
                    onClick={() => { updateActiveDiscount({ type: 'PERCENTAGE', value: 0 }); setDiscountSheetOpen(false); }}
                  >Clear</button>
                  <button type="button" className="posv2-checkout" onClick={() => setDiscountSheetOpen(false)}>Apply</button>
                </div>
              </div>
            </div>
          )}

          {/* Customer sheet */}
          {customerSheetOpen && (
            <div className="posv2-sheet-wrap" onClick={() => setCustomerSheetOpen(false)}>
              <div className="posv2-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Customer info</h3>
                  <button type="button" className="posv2-sheet-close" onClick={() => setCustomerSheetOpen(false)}>
                    <X size={16} />
                  </button>
                </div>
                <input
                  type="text" className="posv2-sheet-input" placeholder="Name"
                  value={activeCart?.customer?.name || ''}
                  onChange={(e) => updateActiveCustomer({ ...(activeCart?.customer || {}), name: e.target.value })}
                />
                <input
                  type="tel" className="posv2-sheet-input" placeholder="Phone"
                  value={activeCart?.customer?.phone || ''}
                  onChange={(e) => updateActiveCustomer({ ...(activeCart?.customer || {}), phone: e.target.value })}
                />
                <input
                  type="email" className="posv2-sheet-input" placeholder="Email (for receipt)"
                  value={activeCart?.customer?.email || ''}
                  onChange={(e) => updateActiveCustomer({ ...(activeCart?.customer || {}), email: e.target.value })}
                />
                <div className="posv2-sheet-actions">
                  <button
                    type="button"
                    className="posv2-foot-pill"
                    onClick={() => { updateActiveCustomer({ name: '', phone: '', email: '' }); setCustomerSheetOpen(false); }}
                  >Clear</button>
                  <button type="button" className="posv2-checkout" onClick={() => setCustomerSheetOpen(false)}>Save</button>
                </div>
              </div>
            </div>
          )}

          {/* Tip sheet */}
          {tipSheetOpen && tipSettings?.enabled && (
            <div className="posv2-sheet-wrap" onClick={() => setTipSheetOpen(false)}>
              <div className="posv2-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Add tip</h3>
                  <button type="button" className="posv2-sheet-close" onClick={() => setTipSheetOpen(false)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="posv2-tip-presets">
                  {[5, 10, 15, 20].map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      className="posv2-tip-preset"
                      onClick={() => updateActiveTip(Math.round(subtotalAfterDiscount * (pct / 100)))}
                    >{pct}%</button>
                  ))}
                </div>
                <input
                  type="number" min="0" className="posv2-sheet-input"
                  placeholder="Custom amount" value={tipAmount || ''}
                  onChange={(e) => updateActiveTip(Number(e.target.value))}
                />
                <div className="posv2-sheet-actions">
                  <button type="button" className="posv2-foot-pill" onClick={() => { updateActiveTip(0); setTipSheetOpen(false); }}>Clear</button>
                  <button type="button" className="posv2-checkout" onClick={() => setTipSheetOpen(false)}>Apply</button>
                </div>
              </div>
            </div>
          )}

          {/* Refund sheet — list recent orders, full-order refund */}
          {refundSheetOpen && (
            <div className="posv2-sheet-wrap" onClick={() => setRefundSheetOpen(false)}>
              <div className="posv2-sheet posv2-sheet--wide" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Refund a recent order</h3>
                  <button type="button" className="posv2-sheet-close" onClick={() => setRefundSheetOpen(false)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="posv2-refund-policy">
                  <label htmlFor="posv2-refund-reason">Refund reason</label>
                  <select
                    id="posv2-refund-reason"
                    value={refundReasonCode}
                    onChange={(e) => setRefundReasonCode(e.target.value)}
                  >
                    {REFUND_REASON_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>{option.label}</option>
                    ))}
                  </select>
                  <p>
                    {REFUND_REASON_OPTIONS.find((option) => option.code === refundReasonCode)?.policy}
                  </p>
                </div>
                <div className="posv2-refund-list">
                  {recentOrders.length === 0 ? (
                    <div className="posv2-empty">No recent orders</div>
                  ) : recentOrders.map((o) => (
                    <div key={o.id} className="posv2-refund-row">
                      <div className="posv2-refund-row-info">
                        <div className="posv2-refund-row-num">#{o.orderNumber || o.id.slice(0, 8)}</div>
                        <div className="posv2-refund-row-meta">{new Date(o.createdAt).toLocaleString()} · {formatMoney(o.total)}</div>
                      </div>
                      <button
                        type="button"
                        className="posv2-foot-pill posv2-foot-pill--danger"
                        onClick={() => processRefund(o.id)}
                      >Refund</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Service-charge fee panel — Owner/Manager only */}
          {feePanelOpen && (
            <FeePanel
              feeSummary={feeSummary}
              formatMoney={formatMoney}
              onClose={() => setFeePanelOpen(false)}
            />
          )}

          {/* First-visit POS discovery prompt. Fires when the merchant
              has no prior posOnboardingChoice — a one-time question so
              online-only merchants can dismiss POS instead of it
              cluttering their nav forever. */}
          {showPOSDiscovery && (
            <div className="posv2-sheet-wrap" onClick={() => !posDiscoverySaving && setShowPOSDiscovery(false)}>
              <div className="posv2-sheet posv2-discovery" onClick={(e) => e.stopPropagation()}>
                <div className="posv2-sheet-head">
                  <h3>Do you use in-person sales?</h3>
                </div>
                <p className="posv2-discovery-body">
                  Point of Sale lets you ring up walk-in customers, print
                  receipts, and track shift analytics. If your business is
                  online-only, you can hide it from your navigation. You
                  can turn it back on anytime in Settings.
                </p>
                <div className="posv2-discovery-actions">
                  <button
                    type="button"
                    className="st-btn st-btn-secondary"
                    disabled={posDiscoverySaving}
                    onClick={() => savePOSChoice('dismissed')}
                  >
                    Not for me
                  </button>
                  <button
                    type="button"
                    className="st-btn st-btn-primary"
                    disabled={posDiscoverySaving}
                    onClick={() => savePOSChoice('active')}
                  >
                    {posDiscoverySaving ? '…' : 'Yes, use POS'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </FeatureGate>
    </Shell>
  );
}
